import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { WatchlistService, WatchlistDTO } from '../services/api'
import { grenzenVon } from '../lib/tarife'
import { Bookmark, Search, ArrowRight } from 'lucide-react'

const WatchlistPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth()
  const navigate = useNavigate()
  const [data, setData] = useState<WatchlistDTO | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  // Bezahlschranke (tarife.py): kostenlose Konten bis 20 Titel, Pro unbegrenzt.
  const grenze = grenzenVon(user).watchlist

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/register', { replace: true })
    }
  }, [isAuthenticated, navigate])

  useEffect(() => {
    const load = async () => {
      if (!isAuthenticated) return
      setLoading(true)
      setError('')
      try {
        const wl = await WatchlistService.getMyWatchlist()
        setData(wl)
      } catch (e: any) {
        setError((e?.message || 'Watchlist konnte nicht geladen werden').toString())
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isAuthenticated])

  const handleRemove = async (symbol: string) => {
    setError('')
    try {
      const wl = await WatchlistService.remove(symbol)
      setData(wl)
    } catch (e: any) {
      setError((e?.message || 'Entfernen fehlgeschlagen').toString())
    }
  }

  const items = data?.items || []

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          Watchlist
        </h1>
        <p className="text-gray-600 dark:text-gray-300">
          Deine gespeicherten Titel – dauerhaft pro Konto.
        </p>
        {grenze !== null && data && (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {items.length} von {grenze} Titeln · mit Pro unbegrenzt
          </p>
        )}
      </div>

      {error && (
        <div className="mb-6 text-center text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center text-gray-600 dark:text-gray-300">Lade Watchlist…</div>
      ) : items.length === 0 ? (
        <div className="max-w-md mx-auto text-center py-12">
          <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center mx-auto mb-5">
            <Bookmark className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Deine Watchlist ist noch leer
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Finde interessante Titel im Screener und speichere sie hier, um sie dauerhaft zu verfolgen.
          </p>
          <Link to="/screener" className="btn-primary px-6 py-3 inline-flex items-center">
            <Search className="w-5 h-5 mr-2" />
            Screener öffnen
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((i) => (
            <div key={i.symbol} className="card-hover p-4">
              <div className="flex justify-between items-start">
                <Link to={`/aktie/${encodeURIComponent(i.symbol)}`} className="q-titlelink">
                  <div className="font-semibold text-gray-900 dark:text-white hover:underline">{i.symbol}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">{i.name}</div>
                </Link>
                <button
                  onClick={() => handleRemove(i.symbol)}
                  className="text-sm text-red-600 hover:underline"
                >
                  Entfernen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default WatchlistPage
